import { ProjectType } from "@prisma/client";
import {
  ImpactType,
  pluralise,
  PublicProjectSummary,
  getPrimaryProjectLocation,
  User,
} from "@ecologi/shared";
import {
  DarkLinkText,
  DisplayProject,
  HowCalculatedModal,
} from "./HowCalculatedModal";
import { Typography } from "../Typography";
import { DiversifyImpactCard } from "../DiversifyImpactCard";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  projectsIncludingTrees: PublicProjectSummary[];
  user: User;
  pendingTotal: number;
};

export const HowCalculatedModalTrees = ({
  projectsIncludingTrees,
  user,
  pendingTotal,
  ...props
}: Props) => {
  const projectTreesContributionAggregate =
    user.projectTreesContributionAggregate || [];

  const rows: DisplayProject[] = projectTreesContributionAggregate.flatMap(
    ({ projectId, treeNumber }) => {
      const currentProject = projectsIncludingTrees.find(
        (p) => p.id === projectId
      );
      if (!currentProject) return [];

      return [
        {
          type: ProjectType.REFORESTATION,
          title: currentProject.title,
          quantity: String(treeNumber),
          location: getPrimaryProjectLocation(currentProject),
          slug: currentProject.slug,
        },
      ];
    }
  );

  const title = (
    <>
      <strong>{user.groupName || user.fullName()}</strong>
      {user.isFamilyPlan ? " have " : " has "}
      funded the planting of{" "}
      <strong>
        {user.totalNumberOfTrees().toLocaleString()}{" "}
        {pluralise(user.totalNumberOfTrees(), "tree", false)}
      </strong>{" "}
      across{" "}
      <strong>
        {rows.length} {pluralise(rows.length, "project", false)}.
      </strong>
    </>
  );

  const pendingTitle = (
    <p data-testid="pending-impact-total">
      *An additional <strong>{pluralise(pendingTotal, "tree")}</strong> has been
      requested and should be added in the next month.
    </p>
  );

  const explainer = (
    <>
      <Typography use="body1" data-testid="why-trees-data">
        Healthy trees sequester CO2, making them climate champions from the
        moment they’re planted. They also help to restore healthy ecosystems,
        sequestering and storing even more carbon in soils and plant biomass, as
        well as boosting biodiversity.
      </Typography>
      <Typography use="body1">
        Reforestation projects provide co-benefits for local people which can
        promote employment, gender equality, new income streams, more diverse
        food sources to support a healthier diet, and improved community
        facilities.
      </Typography>
      <Typography use="body1">
        Ecologi only supports responsible tree planting. This means supporting
        community-led projects where ecologically appropriate species are
        planted at the right place and time, and where trees are guarded from
        natural and man-made threats.
      </Typography>
      <Typography use="body1">
        Importantly, while trees are a wonderful climate solution, the{" "}
        <DarkLinkText
          as="a"
          use="inlineLink"
          target="_blank"
          href="https://ecologi.com/articles/blog/why-we-dont-use-tree-planting-for-carbon-offsetting"
          rel="noopener noreferrer"
        >
          carbon absorbed by our trees is not taken into account as carbon
          avoidance
        </DarkLinkText>{" "}
        here at Ecologi.
      </Typography>
    </>
  );

  const projectsIntro = (
    <Typography use="body1" data-testid="projects-intro-data">
      This breakdown shows how the trees you've funded are distributed across
      our projects. It’s accurate for the UK, US, Australia and Brazil, and
      estimated for all other regions.{" "}
      <DarkLinkText
        as="a"
        use="inlineLink"
        target="_blank"
        href="https://ecologi.com/articles/blog/where-are-my-trees"
        rel="noopener noreferrer"
      >
        Find out more
      </DarkLinkText>{" "}
    </Typography>
  );

  return (
    <HowCalculatedModal
      type={ImpactType.TREES}
      noImpactTitle="You have not yet funded any tree planting projects."
      noImpactSlot={<DiversifyImpactCard impactType={ImpactType.TREES} />}
      title={title}
      pendingTitle={pendingTitle}
      pendingTotal={pendingTotal}
      explanation={explainer}
      projects={rows}
      projectsIntro={projectsIntro}
      {...props}
    ></HowCalculatedModal>
  );
};
